import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Label } from './ui/label';
import { Loader2, FileText } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { format } from 'date-fns';

/**
 * DateRangeSelector Component
 * A reusable date selection component for report pages
 * 
 * @param {Object} props
 * @param {string} props.reportType - Current report type: 'daily', 'weekly', 'monthly', 'yearly', 'custom'
 * @param {Function} props.onReportTypeChange - Callback when report type changes
 * @param {Object} props.selectedDateObj - Selected date object for daily mode
 * @param {Function} props.onDateSelect - Callback for daily date selection
 * @param {number} props.selectedYear - Selected year
 * @param {Function} props.onYearChange - Callback for year change
 * @param {number} props.selectedMonth - Selected month (1-12)
 * @param {Function} props.onMonthChange - Callback for month change
 * @param {number} props.selectedWeek - Selected week number
 * @param {Function} props.onWeekChange - Callback for week change
 * @param {Array} props.weeksInMonth - Array of week objects for the selected month
 * @param {Object} props.customStartDateObj - Custom range start date object
 * @param {Object} props.customEndDateObj - Custom range end date object
 * @param {Function} props.onCustomRangeSelect - Callback for custom range selection
 * @param {Function} props.onGenerateReport - Callback when generate button is clicked
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.getMaxDate - Function to get max selectable date
 * @param {boolean} props.hideGenerateButton - Optional flag to hide the generate button
 * @param {string} props.generateButtonLabel - Optional custom label for the generate button
 */
const DateRangeSelector = ({
  reportType,
  onReportTypeChange,
  selectedDateObj,
  onDateSelect,
  selectedYear,
  onYearChange,
  selectedMonth,
  onMonthChange,
  selectedWeek,
  onWeekChange,
  weeksInMonth = [],
  customStartDateObj,
  customEndDateObj,
  onCustomRangeSelect,
  onGenerateReport,
  loading = false,
  getMaxDate,
  hideGenerateButton = false,
  generateButtonLabel = 'Generate Report',
  excludeReportTypes = []
}) => {
  const allReportTypes = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
    { value: 'custom', label: 'Custom Range' }
  ];
  const reportTypes = excludeReportTypes.length > 0
    ? allReportTypes.filter(t => !excludeReportTypes.includes(t.value))
    : allReportTypes;

  const getTitleAndDescription = () => {
    switch (reportType) {
      case 'daily':
        return { title: 'Select Date', description: 'Choose a specific date for the report' };
      case 'weekly':
        return { title: 'Select Week', description: 'Choose month, year, and specific week' };
      case 'monthly':
        return { title: 'Select Month', description: 'Choose month and year' };
      case 'yearly':
        return { title: 'Select Year', description: 'Choose year' };
      case 'custom':
        return { title: 'Select Date Range', description: 'Drag to select start and end dates' };
      default:
        return { title: '', description: '' };
    }
  };

  const { title, description } = getTitleAndDescription();

  return (
    <div className="space-y-6">
      {/* Report Type Selection - Tabs Style */}
      <div className="flex flex-wrap gap-2 justify-center">
        {reportTypes.map(type => (
          <button
            key={type.value}
            onClick={() => onReportTypeChange(type.value)}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
              reportType === type.value
                ? 'bg-primary text-primary-foreground shadow-md'
                : 'bg-card border border-border hover:bg-accent'
            }`}
          >
            {type.label}
          </button>
        ))}
      </div>

      {/* Calendar Section */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-1">{title}</h3>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>

            <div className="flex justify-center">
              {reportType === 'daily' && (
                <Calendar
                  mode="single"
                  selected={selectedDateObj}
                  onSelect={onDateSelect}
                  disabled={(date) => getMaxDate && date > getMaxDate()}
                  className="rounded-md border shadow-sm"
                  captionLayout="dropdown"
                />
              )}

              {reportType === 'weekly' && (
                <div className="flex flex-col items-center gap-4">
                  <div className="flex gap-4">
                    <div className="space-y-2">
                      <Label>Month</Label>
                      <Select value={selectedMonth.toString()} onValueChange={(value) => onMonthChange(parseInt(value))}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                            <SelectItem key={month} value={month.toString()}>
                              {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Year</Label>
                      <Select value={selectedYear.toString()} onValueChange={(value) => onYearChange(parseInt(value))}>
                        <SelectTrigger className="w-[120px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Week</Label>
                      <Select value={selectedWeek.toString()} onValueChange={(value) => onWeekChange(parseInt(value))}>
                        <SelectTrigger className="w-[200px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {weeksInMonth.map(week => (
                            <SelectItem key={week.weekNumber} value={week.weekNumber.toString()}>
                              {week.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {reportType === 'monthly' && (
                <div className="flex flex-col items-center gap-4">
                  <div className="flex gap-4">
                    <div className="space-y-2">
                      <Label>Month</Label>
                      <Select value={selectedMonth.toString()} onValueChange={(value) => onMonthChange(parseInt(value))}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                            <SelectItem key={month} value={month.toString()}>
                              {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Year</Label>
                      <Select value={selectedYear.toString()} onValueChange={(value) => onYearChange(parseInt(value))}>
                        <SelectTrigger className="w-[120px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {reportType === 'yearly' && (
                <div className="flex flex-col items-center gap-4">
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Select value={selectedYear.toString()} onValueChange={(value) => onYearChange(parseInt(value))}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {reportType === 'custom' && (
                <Calendar
                  mode="range"
                  selected={{ from: customStartDateObj, to: customEndDateObj }}
                  onSelect={onCustomRangeSelect}
                  defaultMonth={customStartDateObj}
                  disabled={(date) => getMaxDate && date > getMaxDate()}
                  className="rounded-md border shadow-sm"
                  captionLayout="dropdown"
                  numberOfMonths={2}
                />
              )}
            </div>

            {/* Selected Period Display for Custom Range */}
            {reportType === 'custom' && customStartDateObj && customEndDateObj && (
              <div className="text-center p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <p className="font-medium text-primary">
                  {format(customStartDateObj, 'dd MMM yyyy')} - {format(customEndDateObj, 'dd MMM yyyy')}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {Math.ceil((customEndDateObj - customStartDateObj) / (1000 * 60 * 60 * 24)) + 1} days
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Generate Report Button */}
      {!hideGenerateButton && (
        <div className="flex justify-center">
          <Button 
            onClick={onGenerateReport} 
            disabled={loading}
            size="lg"
            className="min-w-[200px] gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FileText className="h-5 w-5" />
                {generateButtonLabel}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;

